import React from "react"
import { useBlocks } from "app/regions/hooks/use-blocks"
import { useEditing } from "app/regions/hooks/use-editing"
import { getBlockByType } from "app/regions/blocks/utils"
import { OutlookVerticalSpacer } from "../header/utils/outlook-vertical-spacer"
import { InheritedThemeProvider } from "@convertkit/editor"
import { compact } from "core/utils"
import { getBackgroundImage } from "./utils/get-background-image"
import { getBorderStyle } from "app/regions/blocks/utils"

export function Section({ id }) {
  const { properties, children } = useBlocks(id)
  const editing = useEditing()

  const containerStyles = compact({
    backgroundColor: properties.backgroundColor,
    backgroundSize: "cover",
    backgroundPosition: "center",
    backgroundImage: getBackgroundImage(properties),
    marginTop: properties.marginTop,
    marginRight: properties.marginRight,
    marginBottom: properties.marginBottom,
    marginLeft: properties.marginLeft,
    borderTopLeftRadius: properties.borderTopLeftRadius,
    borderTopRightRadius: properties.borderTopRightRadius,
    borderBottomLeftRadius: properties.borderBottomLeftRadius,
    borderBottomRightRadius: properties.borderBottomRightRadius,
    borderTop: getBorderStyle(
      properties.borderTopWidth,
      properties.borderColor
    ),
    borderRight: getBorderStyle(
      properties.borderRightWidth,
      properties.borderColor
    ),
    borderBottom: getBorderStyle(
      properties.borderBottomWidth,
      properties.borderColor
    ),
    borderLeft: getBorderStyle(
      properties.borderLeftWidth,
      properties.borderColor
    ),
    boxSizing: "border-box"
  })

  const hasMarginBottom = containerStyles.marginBottom !== 0

  const innerSectionStyles = {
    paddingTop: properties.paddingTop,
    paddingRight: properties.paddingRight,
    paddingBottom: properties.paddingBottom,
    paddingLeft: properties.paddingLeft
  }

  // We don't want sections to have too much padding on mobile. If it's applied,
  // we hardcode it to 18px on smaller screens. Otherwise, we leave it off so
  // the content is as close to the edge of the screen as possible.
  const mobileStyles = `
    @media only screen and (max-width:600px) {
      .ck-inner-section {
        padding-top: ${properties.paddingTop ? "18px" : "0px"} !important;
        padding-right: ${properties.paddingRight ? "18px" : "0px"} !important;
        padding-bottom: ${properties.paddingBottom ? "18px" : "0px"} !important;
        padding-left: ${properties.paddingLeft ? "18px" : "0px"} !important;
      }
    }
  `

  return (
    <InheritedThemeProvider theme={properties.theme}>
      <div data-block-id={id} data-section-block-id={id} className="ck-section">
        <center>
          <table
            cellPadding="0"
            cellSpacing="0"
            style={{
              width: "100%",
              margin: "0 auto",
              maxWidth: properties.maxWidth
            }}
          >
            <tbody>
              <tr>
                {/* Empty TDs are used to center the section in Outlook */}
                <td />
                <td
                  className="ck-section-td"
                  width={properties.maxWidth}
                  style={containerStyles}
                  bgcolor={
                    properties.backgroundImage
                      ? null
                      : properties.backgroundColor
                  }
                  background={properties.backgroundImage || null}
                >
                  <div className="ck-inner-section" style={innerSectionStyles}>
                    <div
                      style={{
                        marginLeft: "auto",
                        marginRight: "auto",
                        maxWidth: properties.contentWidth
                      }}
                    >
                      {children.map(({ type, block_id, ...block }) => {
                        const config = getBlockByType(type)

                        const editorVersion = config.component
                        const emailVersion = config.email || config.component
                        const Component = editing ? editorVersion : emailVersion

                        return (
                          <Component {...block} key={block_id} id={block_id} />
                        )
                      })}
                    </div>
                  </div>
                </td>
                <td />
              </tr>
            </tbody>
          </table>
          {hasMarginBottom && <OutlookVerticalSpacer height={2} />}
        </center>
        <style dangerouslySetInnerHTML={{ __html: mobileStyles }} />
      </div>
    </InheritedThemeProvider>
  )
}
